import {TemplateResult, html, css} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {msg, localized} from '@lit/localize';
import ToolPanelElement from 'gmfapi/elements/ToolPanelElement';
import configModel from 'gmfapi/store/config';
import mapModel from 'gmfapi/store/map';
import panels from 'gmfapi/store/panels';
import Interaction from 'ol/Interaction';
import Map from 'ol/Map.js';
import View from 'ol/View';
import {listen} from 'ol/events';
import {BehaviorSubject, Subscription, combineLatest} from 'rxjs';

import {rectangleInteraction, polygonInteraction} from './extent';
import {IProduct} from '../../services/geoshop_api';
import {User} from 'oidc-client-ts';
import geoshop from '../../services/geoshop';
import geoshopOrder from '../../services/geoshop_order';

@localized()
@customElement('geoshop-panel')
export class ToolButtonGeoshopPanel extends ToolPanelElement {
  @property({type: String}) lang: string = 'de';
  @property() products: IProduct[] = [];
  @state() user: User | null = null;
  private view?: View;
  private interaction?: Interaction;
  private subscriptions: Subscription[] = [];
  geoshopUrl: BehaviorSubject<string> = new BehaviorSubject('');

  constructor() {
    super('geo-shop-open');
    const configSubscription = configModel.getConfig().subscribe((config) => {
      if (!config) {
        return;
      }
      if (config.ugspGeoshopApiUrl) {
        geoshop.setApiUrl(config.ugspGeoshopApiUrl);
      }
      if (config.ugspGeoshopUrl) {
        if (!config.ugspGeoshopApiUrl) {
          geoshop.setApiUrl(`${config.ugspGeoshopUrl}api/`);
        }
        this.geoshopUrl.next(config.ugspGeoshopUrl);
      }
      configSubscription.unsubscribe();
    });
  }

  async connectedCallback(): void {
    super.connectedCallback();
    this.subscriptions.push(
      mapModel.getMap().subscribe((map: Map) => {
        if (!map) {
          return;
        }
        this.map = map;
        this.view = map.getView();
      }),
      panels.getActiveToolPanel().subscribe((panelName) => {
        // possibility if geoshop product list shall be displayed on the left
        // this.switchToLeft(panelName == 'geoshop')
        if (panelName == 'geoshop') {
          geoshopOrder.showOverlay();
        } else {
          geoshopOrder.hideOverlay();
        }
      }),
      combineLatest(this.geoshopUrl, panels.getActiveToolPanel()).subscribe(
        ([url, panelName]: [string, string]) => {
          if (url !== '' && panelName == 'geoshop') {
            this.loadItems();
          }
        },
      ),
      geoshop.user.subscribe((user) => (this.user = user)),
    );
    geoshop.loginWhenReady();
  }

  switchToLeft(left: boolean) {
    if (left) {
      document.getElementsByTagName(
        'gmf-desktop-canvas',
      )[0].shadowRoot.children[2].children[0].children[0].children[0].name = 'tool-panel-geoshop-products';
      this.classList.remove('row');
    } else {
      document.getElementsByTagName(
        'gmf-desktop-canvas',
      )[0].shadowRoot.children[2].children[0].children[0].children[0].name = 'data';
      this.classList.add('row');
    }
  }

  loadItems(): void {
    geoshop.getProducts().then((p) => {
      this.products = [...p.results];
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  clickStore() {
    const url = this.generateGeoshopUrl(
      this.geoshopUrl.value,
      this.view?.getViewStateAndExtent().extent as [],
    );
    window.open(url, '_blank');
  }

  async login() {
    await geoshop.login({forceSelect: true});
    await geoshop.getUser();
  }

  logout() {
    geoshop.logout();
  }

  addProduct(product: IProduct) {
    geoshopOrder.addProduct(product);
  }

  setInteraction(interaction: Interaction) {
    const oldInteraction = this.interaction;
    if (oldInteraction) {
      this.map.removeInteraction(oldInteraction);
    }
    this.interaction = interaction;
    listen(this.interaction, 'drawend', this.setExtent, this);
    this.map.addInteraction(this.interaction);
  }

  defineExtent() {
    this.setInteraction(rectangleInteraction());
  }

  clearExtent() {
    geoshopOrder.setExtent(null);
  }

  defineRectangle() {
    this.setInteraction(rectangleInteraction());
  }

  definePolygon() {
    this.setInteraction(polygonInteraction());
  }

  setExtent(event) {
    console.log(event.feature);
    geoshopOrder.setExtent(event.feature.getGeometry());
    this.map.removeInteraction(this.interaction);
    return true;
  }

  generateGeoshopUrl(baseUrl: string, extent: [] | undefined): string {
    return `${baseUrl}de/welcome?bounds=${extent}`;
  }

  createShopItem(product: IProduct) {
    return html` <li>
      <button
        part="add"
        @click=${() => {
          this.addProduct(product);
        }}
        class="add-cart geogr-button btn btn-default ${this.active_ ? 'active' : ''}"
        data-toggle="tooltip"
        data-placement="left"
        data-original-title="${msg('Order in GeoShop')}"
      >
        <span class="fa-add-cart"></span>
      </button>
      ${product.label}
    </li>`;
  }

  static styles = css`
    button[part='add'] {
      width: 2.3rem !important;
      height: 2.3rem !important;
      padding: 0 !important;
      padding-top: 0.3rem !important;
      margin: 0 auto;
    }
  `;

  extentSelector(): TemplateResult {
    return html`
      <div class="extent-tools">
        <span>EXTENT</span>
        <button
          id="define-polygon"
          @click=${() => {
            this.definePolygon();
          }}
        ></button>
        <button
          id="define-rect"
          @click=${() => {
            this.defineRectangle();
          }}
        ></button>
        <button
          id="clear-extent"
          @click=${() => {
            this.clearExtent();
          }}
        ></button>
      </div>
    `;
  }

  render(): TemplateResult {
    return html`
      <div class="geoshop">
        <div>
          <div class="geoshop-heading">
            ${msg('GeoGR Shop')} ${this.user === null ? 'logged out' : this.user.profile.name}
            <span class="placeholder"> </span>
            <button class="close-button" @click="${() => panels.closeToolPanel()}">×</button>
          </div>
          <hr />

          <div>
            <button
              part="button"
              @click=${() => {
                this.clickStore();
              }}
              class="btn btn-default ${this.active_ ? 'active' : ''}"
              data-toggle="tooltip"
              data-placement="left"
              title="${msg('Go to GeoShop')}"
            >
              <span>geoshop</span>
            </button>
            <button
              @click=${() => {
                this.login();
              }}
              class="btn btn-default ${this.active_ ? 'active' : ''}"
              data-toggle="tooltip"
              data-placement="left"
              title="${msg('Log into shop')}"
            >
              LOGIN
            </button>
            <button
              part="button"
              @click=${() => {
                this.logout();
              }}
              class="btn btn-default ${this.active_ ? 'active' : ''}"
              data-toggle="tooltip"
              data-placement="left"
              title="${msg('Disconnect all users')}"
            >
              LOGOUT
            </button>
          </div>
          <hr />
        </div>
        <div id="geoshop-products">
          <ul>
            ${this.products.map((p: IProduct) => this.createShopItem(p))}
          </ul>
        </div>
        <div>
          <hr />
          ${this.extentSelector()}
          <hr />
          <hr />
        </div>
        <geoshop-cart slot="tool-panel-geoshop"> </geoshop-cart>
        <geoshop-order slot="tool-panel-geoshop"> </geoshop-order>
      </div>
    `;
  }
}
